import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { checkNonZeroArray } from '../../helper';
import { DashboardType, MetricsType, MetricsView } from '../../types';
import { EPType } from '../config';
import { appliedEPIdsAtom, epTypeAtom, severityFiltersAtom } from '../state';

export const isMetricsSectionOpenAtom = atom(false);
export const metricsViewAtom = atomWithStorage(
  'ep_metrics_view',
  MetricsView.Severity,
);
export const timeViewLabels = [
  '0 to 3 Days',
  '3+ to 7 Days',
  '7+ to 14 Days',
  '14+ to 28 Days',
  '28+ Days',
];

export const vulnMetricsDataAtom = atom(async (get) => {
  const isOpen = get(isMetricsSectionOpenAtom);
  if (isOpen) {
    const companyId = get(companyIdAtom);
    const selectedEPIds = get(appliedEPIdsAtom);

    try {
      if (companyId && companyId !== -1) {
        const params = {
          company_id: companyId,
          host_type: DashboardType.EP,
        };
        if (selectedEPIds?.length > 0) {
          params['obj_id_list'] = selectedEPIds;
        }

        const responseData = await callApi('dashboard/metrics', 'GET', params);
        return responseData?.data;
      }
    } catch (error) {
      console.error(error);
    }
  }
  return undefined;
});

export const openVulnMetricsBySeverityAverageDayTotalVulnAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      epType,
      MetricsType.Open,
      'Days',
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsBySeverityCountsAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      epType,
      MetricsType.Open,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const closeVulnMetricsBySeverityAverageDayTotalVulnAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      epType,
      MetricsType.Closed,
      'Days',
      severityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsBySeverityCountsAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      epType,
      MetricsType.Closed,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsByTimeCountsAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      epType,
      MetricsType.Open,
      severityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsByTimeCountsAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      epType,
      MetricsType.Closed,
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsTooltipAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      epType,
      MetricsType.Open,
      'Days',
    );
  }
  return [];
});

export const closedVulnMetricsTooltipAtom = atom((get) => {
  const data: any = get(vulnMetricsDataAtom);
  const epType = get(epTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      epType,
      MetricsType.Closed,
      'Days',
    );
  }
  return [];
});

function getVulnMetrics(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  propertyName: string,
  severityFilter: any,
) {
  const internalNode = sourceData[EPType.Internal][metricsType];
  const externalNode = sourceData[EPType.External][metricsType];
  const ec2Node = sourceData[EPType.EC2][metricsType];

  const internalValues: any[] = Object.values(internalNode);
  const externalValues: any[] = Object.values(externalNode);
  const ec2Values: any[] = Object.values(ec2Node);

  const internalData = internalValues.map((x) => x[propertyName]);
  const externalData = externalValues.map((x) => x[propertyName]);
  const ec2Data = ec2Values.map((x) => x[propertyName]);

  const resultsArray: number[][] = [];
  if (
    (epType === EPType.Internal || epType === EPType.All) &&
    checkNonZeroArray(internalData)
  )
    resultsArray.push(internalData);
  if (
    (epType === EPType.External || epType === EPType.All) &&
    checkNonZeroArray(externalData)
  )
    resultsArray.push(externalData);
  if (
    (epType === EPType.EC2 || epType === EPType.All) &&
    checkNonZeroArray(ec2Data)
  )
    resultsArray.push(ec2Data);

  const averageDaysOpenClose = getVulnMetricsAverageDaysOpenClose(
    sourceData,
    epType,
    metricsType,
    severityFilter,
  );
  const totalVulnsCount = getVulnMetricsTotalVulnsCount(
    sourceData,
    epType,
    metricsType,
    severityFilter,
  );
  return {
    metricsData: resultsArray,
    averageDaysOpenClose: averageDaysOpenClose,
    totalVulnsCount: totalVulnsCount,
  };
}

function getVulnMetricsTotalVulnsCount(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  severityFilters: string[],
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    epType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  return totalCount;
}

function getVulnMetricsAverageDaysOpenClose(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  let filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    epType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalDays = validVulnerabilities.reduce(
    (sum, v) => sum + v.Days * v.Count,
    0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  const averageDaysOpenClose = totalCount > 0 ? totalDays / totalCount : 0;
  return averageDaysOpenClose.toFixed(1);
}

function filterMetricsBySeverity(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const internalNode = sourceData[EPType.Internal][metricsType];
  const externalNode = sourceData[EPType.External][metricsType];
  const ec2Node = sourceData[EPType.EC2][metricsType];

  const severities = ['Urgent', 'Critical', 'Medium', 'Minimal', 'Serious'];

  let resultArray: any[] = [];
  severities.forEach((severity) => {
    if (severityFilters.includes(severity)) {
      if (epType === EPType.Internal || epType === EPType.All) {
        const internalValues = internalNode[severity];
        resultArray.push(internalValues);
      }
      if (epType === EPType.External || epType === EPType.All) {
        const externalValues = externalNode[severity];
        resultArray.push(externalValues);
      }
      if (epType === EPType.EC2 || epType === EPType.All) {
        const ec2Values = ec2Node[severity];
        resultArray.push(ec2Values);
      }
    }
  });
  return resultArray;
}

function getVulnMetricsByTimeData(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  severityFilter: any,
) {
  let internalData: number[] = [];
  let externalData: number[] = [];
  let ec2Data: number[] = [];

  severityFilter.forEach((severity) => {
    const internalNode = sourceData[EPType.Internal][metricsType][severity];
    const externalNode = sourceData[EPType.External][metricsType][severity];
    const ec2Node = sourceData[EPType.EC2][metricsType][severity];

    const internalValues: any[] = Object.values(internalNode);
    const externalValues: any[] = Object.values(externalNode);
    const ec2Values: any[] = Object.values(ec2Node);

    for (var i = 0; i < internalValues.length; i++) {
      if (internalData[i]) internalData[i] += internalValues[i];
      else internalData[i] = internalValues[i];

      if (externalData[i]) externalData[i] += externalValues[i];
      else externalData[i] = externalValues[i];

      if (ec2Data[i]) ec2Data[i] += ec2Values[i];
      else ec2Data[i] = ec2Values[i];
    }
  });

  const resultsArray: number[][] = [];
  if (
    (epType === EPType.Internal || epType === EPType.All) &&
    checkNonZeroArray(internalData)
  ) {
    resultsArray.push(internalData);
  }
  if (
    (epType === EPType.External || epType === EPType.All) &&
    checkNonZeroArray(externalData)
  ) {
    resultsArray.push(externalData);
  }

  if (
    (epType === EPType.EC2 || epType === EPType.All) &&
    checkNonZeroArray(ec2Data)
  ) {
    resultsArray.push(ec2Data);
  }
  return resultsArray;
}

function getVulnMetricsTooltips(
  sourceData: any,
  epType: EPType,
  metricsType: MetricsType,
  propertyName: string,
) {
  const internalNode = sourceData[EPType.Internal][metricsType];
  const externalNode = sourceData[EPType.External][metricsType];
  const ec2Node = sourceData[EPType.EC2][metricsType];

  const internalValues: any[] = Object.values(internalNode);
  const externalValues: any[] = Object.values(externalNode);
  const ec2Values: any[] = Object.values(ec2Node);

  const internalData = internalValues.map((x) => x[propertyName]);
  const externalData = externalValues.map((x) => x[propertyName]);
  const ec2Data = ec2Values.map((x) => x[propertyName]);

  const toolTipLabels: string[] = [];
  if (
    (epType === EPType.Internal || epType === EPType.All) &&
    checkNonZeroArray(internalData)
  ) {
    toolTipLabels.push(EPType.Internal);
  }
  if (
    (epType === EPType.External || epType === EPType.All) &&
    checkNonZeroArray(externalData)
  ) {
    toolTipLabels.push(EPType.External);
  }

  if (
    (epType === EPType.EC2 || epType === EPType.All) &&
    checkNonZeroArray(ec2Data)
  ) {
    // toolTipLabels.push(EPType.EC2);
    toolTipLabels.push('AWS');
  }
  return toolTipLabels;
}
